// src/features/profile/Sponsoring/components/PaymentDialog.tsx

import React, { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { InlineIcon } from "@iconify/react";
import { TSubscriptionPlan } from "@/features/profile/types/types";
import PaypalCardFields, {
  PaypalCardApproveData,
} from "@/components/PaypalCardFields/PaypalCardFields";

type PaymentDialogProps = {
  open: boolean;
  offer: TSubscriptionPlan | null;
  onClose: () => void;
  /** Paiement direct via le compte PayPal (redirection wallet). */
  onPayByPaypalWallet: () => Promise<void>;
  /** Paiement via Stripe Checkout (redirection). */
  onPayByStripe: () => void;
  /** Crée l'ordre PayPal (mode CARD) et renvoie l'order id. */
  paypalCardCreateOrder: () => Promise<string>;
  /** Capture l'ordre après saisie carte + 3DS, puis redirige. */
  paypalCardOnApprove: (data: PaypalCardApproveData) => Promise<void>;
  getPriceByGender: (offer: TSubscriptionPlan) => number;
};

/**
 * Fenêtre modale proposant le choix du mode de paiement.
 * - « Payer par carte bancaire » avec logo PayPal : saisie carte via PayPal
 *   Card Fields, sans quitter le site.
 * - « Payer par PayPal » : redirection vers le compte PayPal (wallet).
 * - « Payer par carte bancaire » avec logos CB : redirection Stripe Checkout.
 */
const PaymentDialog: FC<PaymentDialogProps> = ({
  open,
  offer,
  onClose,
  onPayByPaypalWallet,
  onPayByStripe,
  paypalCardCreateOrder,
  paypalCardOnApprove,
  getPriceByGender,
}) => {
  const [view, setView] = useState<"choice" | "paypalCard" | "stripeInfo">("choice");
  const [isPayingByWallet, setIsPayingByWallet] = useState(false);

  // Revenir au choix des modes à chaque ouverture.
  useEffect(() => {
    if (open) {
      setView("choice");
      setIsPayingByWallet(false);
    }
  }, [open]);

  if (!offer) return null; // Pas d'offre sélectionnée => ne rien afficher

  const price = getPriceByGender(offer);

  const handlePaypalWalletClick = async () => {
    setIsPayingByWallet(true);
    try {
      await onPayByPaypalWallet();
    } finally {
      setIsPayingByWallet(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="payment-dialog-title"
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 2, p: { xs: 1, sm: 2 } } }}
    >
      <DialogTitle
        id="payment-dialog-title"
        sx={{ color: "var(--ui-dark)", fontWeight: "bold" }}
      >
        Paiement de l'offre
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: "var(--ui-medium)" }} component="div">
          <Typography variant="subtitle1" fontWeight="bold">
            {offer.name}
          </Typography>
          <Typography variant="body2" mb={2}>
            {offer.description}
          </Typography>

          <Typography variant="body1">
            Montant à payer :{" "}
            <b>
              {price}
              {offer.currency === "EUR" && " €"}
              {offer.currency === "USD" && " $"}
            </b>
          </Typography>

          <Typography variant="body1">
            Durée : <b>{offer.duration}</b>
          </Typography>

          {(view === "choice" || view === "stripeInfo") && (
            <Typography variant="body2" mt={2}>
              Veuillez sélectionner votre mode de paiement :
            </Typography>
          )}

          {view === "stripeInfo" && (
            <Typography variant="body2" mt={2}>
              <b>⚠️ Information importante</b>
              <br />
              <br />
              Votre abonnement vous donne accès aux services de Zawaj Sounnah,
              un service exploité par Family Academy.
              <br />
              <br />
              Votre paiement sera encaissé par Family Academy et apparaîtra
              sous le nom « FAMILY ACADEMY » sur votre relevé bancaire.
              <br />
              Après paiement votre accès à la plate-forme sera débloquée
            </Typography>
          )}
        </DialogContentText>

        {view === "paypalCard" && (
          <PaypalCardFields
            createOrder={paypalCardCreateOrder}
            onApprove={paypalCardOnApprove}
            submitLabel={`Payer ${price}${offer.currency === "EUR" ? " €" : ""}`}
            currency={offer.currency || "EUR"}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          pb: 3,
        }}
      >
        {view === "choice" ? (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setView("paypalCard")}
              disabled={isPayingByWallet}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              Payer par carte bancaire&nbsp;&nbsp;
              <img
                src={require("@/assets/images/png/paypal.png")}
                alt="paypal"
                width={50}
                style={{ verticalAlign: "middle" }}
              />
            </Button>

            <Button
              variant="contained"
              color="tertiary"
              onClick={handlePaypalWalletClick}
              disabled={isPayingByWallet}
              startIcon={
                isPayingByWallet ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <InlineIcon icon="logos:paypal-icon" />
                )
              }
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              {isPayingByWallet ? "Redirection en cours..." : "Payer par PayPal"}
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={() => setView("stripeInfo")}
              disabled={isPayingByWallet}
              startIcon={<InlineIcon icon="mdi:credit-card-outline" />}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              Payer par carte bancaire&nbsp;&nbsp;
              <img
                src={require("@/assets/images/png/pay-by-cb.png")}
                alt="pay-by-cb"
                width={50}
                style={{ verticalAlign: "middle" }}
              />
            </Button>

            {/* Bouton Annuler */}
            <Button onClick={onClose} disabled={isPayingByWallet} sx={{ textTransform: "none" }}>
              Annuler
            </Button>
          </>
        ) : view === "paypalCard" ? (
          <Button
            onClick={() => setView("choice")}
            startIcon={<InlineIcon icon="mdi:arrow-left" />}
            sx={{ textTransform: "none" }}
          >
            Retour
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="primary"
              onClick={onPayByStripe}
              startIcon={<InlineIcon icon="mdi:credit-card-outline" />}
              sx={{ textTransform: "none", fontWeight: 500 }}
            >
              Payer
            </Button>
            <Button
              onClick={() => setView("choice")}
              startIcon={<InlineIcon icon="mdi:arrow-left" />}
              sx={{ textTransform: "none" }}
            >
              Retour
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentDialog;
